import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
};

const TABS = ['Stock', 'Purchase Orders', 'Transactions', 'Settings'];
const CATEGORIES = ['Vegetables','Fruits','Dairy','Meat & Poultry','Seafood','Grains & Pulses','Spices','Oils & Fats','Beverages','Dry Goods','Bakery','Cleaning','Other'];
const UNITS = ['kg','g','L','ml','pcs','dozen','box','pack','bag','bottle'];

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const inp = {
  width:'100%', padding:'9px 12px', background:'#F3F6F3',
  border:'1.5px solid #CDD8CD', borderRadius:8,
  color:'#1A2E1E', fontSize:13, boxSizing:'border-box',
};

const lbl = (t) => <div style={{fontSize:11,fontWeight:700,color:'#7A9A80',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>{t}</div>;

const Badge = ({children, color='accent'}) => {
  const colors = {
    accent:{bg:C.accentLight,color:C.accent},
    red:{bg:C.redBg,color:C.red},
    amber:{bg:C.amberBg,color:C.amber},
    blue:{bg:C.blueBg,color:C.blue},
    gray:{bg:C.bg,color:C.text3},
  };
  const s = colors[color]||colors.accent;
  return <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,...s}}>{children}</span>;
};

export default function Inventory() {
  const [tab, setTab] = useState('Stock');
  const [items, setItems] = useState([]);
  const [pos, setPOs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterAlert, setFilterAlert] = useState('');
  const [msg, setMsg] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showPOForm, setShowPOForm] = useState(false);
  const [showReceive, setShowReceive] = useState(null);
  const [autoPoResult, setAutoPoResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const initForm = { item_name:'', category:'Vegetables', unit:'kg', current_stock:0, reorder_level:0, max_stock:'', average_cost:0, last_purchase_price:'', vendor_name:'', vendor_phone:'', vendor_email:'', lead_time_days:1, expiry_tracking_required:false };
  const [form, setForm] = useState(initForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const initPO = { vendor_name:'', vendor_phone:'', vendor_email:'', department:'Kitchen', required_date:'', notes:'', items:[] };
  const [poForm, setPOForm] = useState(initPO);
  const [poItems, setPOItems] = useState([]);

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, posRes] = await Promise.all([
        fetch(`${API}/inventory`).then(r=>r.json()),
        fetch(`${API}/kitchen/purchase-orders`).then(r=>r.json()),
      ]);
      setItems(Array.isArray(invRes)?invRes:[]);
      setPOs(Array.isArray(posRes)?posRes:[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(()=>{ loadAll(); },[loadAll]);

  const saveItem = async () => {
    if (!form.item_name) { showMsg('Item name required'); return; }
    setSaving(true);
    try {
      const url = editItem ? `${API}/inventory/${editItem.id}` : `${API}/inventory`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
      if (r.ok) { setShowAdd(false); setEditItem(null); setForm(initForm); loadAll(); showMsg(editItem?'Item updated!':'Item added!'); }
      else { const d=await r.json(); showMsg(d.error||'Failed'); }
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${API}/inventory/${id}`, {method:'DELETE'});
    loadAll(); showMsg('Deleted');
  };

  const adjustStock = async (item, qty, type) => {
    const newStock = type==='add' ? parseFloat(item.current_stock)+parseFloat(qty) : Math.max(0, parseFloat(item.current_stock)-parseFloat(qty));
    await fetch(`${API}/inventory/${item.id}`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({...item, current_stock: newStock})
    });
    loadAll();
  };

  const autoGeneratePO = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/kitchen/purchase-orders/auto-generate`, {method:'POST', headers:{'Content-Type':'application/json'}});
      const d = await r.json();
      if (r.ok) {
        setAutoPoResult(d);
        loadAll();
        showMsg(`Auto PO generated: ${d.po?.po_number} — ${d.items?.length} items`);
        setTab('Purchase Orders');
      } else showMsg(d.message || d.error || 'No low stock items');
    } catch(e) { showMsg('Error generating PO'); }
    setGenerating(false);
  };

  const updatePOStatus = async (id, status) => {
    await fetch(`${API}/kitchen/purchase-orders/${id}/status`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({ status, approved_by: 'Manager' })
    });
    loadAll(); showMsg(`PO ${status}`);
  };

  const savePO = async () => {
    if (!poItems.length) { showMsg('Add at least one item'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/kitchen/purchase-orders`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ ...poForm, items: poItems })
      });
      if (r.ok) { setShowPOForm(false); setPOForm(initPO); setPOItems([]); loadAll(); showMsg('PO created!'); }
      else showMsg('Failed to create PO');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const receivePO = async (po, receivedItems) => {
    await fetch(`${API}/kitchen/purchase-orders/${po.id}/receive`, {
      method:'POST', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({ items: receivedItems })
    });
    setShowReceive(null); loadAll(); showMsg('Stock received and updated!');
  };

  const lowStock = items.filter(i => parseFloat(i.current_stock) <= parseFloat(i.reorder_level||0) && parseFloat(i.reorder_level||0) > 0);
  const outOfStock = items.filter(i => parseFloat(i.current_stock) <= 0);

  const filtered = items.filter(i => {
    const matchSearch = !search || i.item_name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || i.category === filterCat;
    const matchAlert = !filterAlert ||
      (filterAlert==='low' && parseFloat(i.current_stock) <= parseFloat(i.reorder_level||0) && parseFloat(i.reorder_level||0)>0) ||
      (filterAlert==='out' && parseFloat(i.current_stock) <= 0) ||
      (filterAlert==='ok' && parseFloat(i.current_stock) > parseFloat(i.reorder_level||0));
    return matchSearch && matchCat && matchAlert;
  });

  const getStockStatus = (item) => {
    const stock = parseFloat(item.current_stock||0);
    const reorder = parseFloat(item.reorder_level||0);
    if (stock <= 0) return {label:'Out of Stock', color:'red'};
    if (reorder > 0 && stock <= reorder) return {label:'Low Stock', color:'amber'};
    return {label:'In Stock', color:'accent'};
  };

  const ItemForm = () => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:600,maxHeight:'90vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{editItem?'Edit Item':'Add Inventory Item'}</div>
          <button onClick={()=>{setShowAdd(false);setEditItem(null);setForm(initForm);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Cancel</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div style={{gridColumn:'span 2'}}>{lbl('Item Name *')}<input style={inp} value={form.item_name} onChange={e=>set('item_name',e.target.value)} placeholder="e.g. Basmati Rice" /></div>
          <div>{lbl('Category')}<select style={inp} value={form.category} onChange={e=>set('category',e.target.value)}>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</select></div>
          <div>{lbl('Unit')}<select style={inp} value={form.unit} onChange={e=>set('unit',e.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select></div>
          <div>{lbl('Current Stock')}<input style={inp} type="number" value={form.current_stock} onChange={e=>set('current_stock',e.target.value)} /></div>
          <div>{lbl('Reorder Level (Alert)')}<input style={inp} type="number" value={form.reorder_level} onChange={e=>set('reorder_level',e.target.value)} /></div>
          <div>{lbl('Max Stock')}<input style={inp} type="number" value={form.max_stock} onChange={e=>set('max_stock',e.target.value)} placeholder="Optional" /></div>
          <div>{lbl('Average Cost (Rs)')}<input style={inp} type="number" value={form.average_cost} onChange={e=>set('average_cost',e.target.value)} /></div>
          <div>{lbl('Last Purchase Price')}<input style={inp} type="number" value={form.last_purchase_price} onChange={e=>set('last_purchase_price',e.target.value)} /></div>
          <div>{lbl('Lead Time (Days)')}<input style={inp} type="number" value={form.lead_time_days} onChange={e=>set('lead_time_days',e.target.value)} /></div>
        </div>

        <div style={{padding:'12px 14px',background:C.bg,borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:10}}>Vendor Details</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            <div>{lbl('Vendor Name')}<input style={inp} value={form.vendor_name} onChange={e=>set('vendor_name',e.target.value)} placeholder="Supplier name" /></div>
            <div>{lbl('Phone')}<input style={inp} value={form.vendor_phone} onChange={e=>set('vendor_phone',e.target.value)} /></div>
            <div>{lbl('Email')}<input style={inp} value={form.vendor_email} onChange={e=>set('vendor_email',e.target.value)} /></div>
          </div>
        </div>

        <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:16,cursor:'pointer'}}>
          <input type="checkbox" checked={form.expiry_tracking_required} onChange={e=>set('expiry_tracking_required',e.target.checked)} />
          <span style={{fontSize:13,color:C.text2,fontWeight:600}}>Expiry Tracking Required</span>
        </label>

        <button onClick={saveItem} disabled={saving}
          style={{width:'100%',padding:'13px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',opacity:saving?0.6:1}}>
          {saving?'Saving...':editItem?'Update Item':'Add Item'}
        </button>
      </div>
    </div>
  );

  const POForm = () => {
    const addPOItem = (item) => {
      if (poItems.find(i=>i.inventory_item_id===item.id)) return;
      const reorderQty = Math.max(parseFloat(item.reorder_level||0)*2 - parseFloat(item.current_stock||0), parseFloat(item.reorder_level||0));
      setPOItems(p=>[...p, {
        inventory_item_id: item.id, item_name: item.item_name,
        unit: item.unit, current_stock: item.current_stock,
        reorder_level: item.reorder_level,
        quantity_ordered: reorderQty,
        unit_price: item.last_purchase_price||item.average_cost||0,
        total_price: reorderQty * (item.last_purchase_price||item.average_cost||0),
      }]);
    };
    const updatePOItem = (idx, k, v) => {
      setPOItems(p => p.map((item,i) => {
        if (i!==idx) return item;
        const updated = {...item, [k]:v};
        if (k==='quantity_ordered'||k==='unit_price') {
          updated.total_price = parseFloat(updated.quantity_ordered||0) * parseFloat(updated.unit_price||0);
        }
        return updated;
      }));
    };

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
        <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:700,maxHeight:'90vh',overflowY:'auto',padding:28}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
            <div style={{fontSize:17,fontWeight:800,color:C.text}}>Create Purchase Order</div>
            <button onClick={()=>{setShowPOForm(false);setPOItems([]);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer',color:C.text2}}>Cancel</button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
            <div>{lbl('Vendor Name')}<input style={inp} value={poForm.vendor_name} onChange={e=>setPOForm(p=>({...p,vendor_name:e.target.value}))} placeholder="Supplier" /></div>
            <div>{lbl('Phone')}<input style={inp} value={poForm.vendor_phone} onChange={e=>setPOForm(p=>({...p,vendor_phone:e.target.value}))} /></div>
            <div>{lbl('Required Date')}<input style={inp} type="date" value={poForm.required_date} onChange={e=>setPOForm(p=>({...p,required_date:e.target.value}))} /></div>
          </div>
          <div style={{marginBottom:14}}>{lbl('Notes')}<input style={inp} value={poForm.notes} onChange={e=>setPOForm(p=>({...p,notes:e.target.value}))} placeholder="Special instructions..." /></div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:8}}>Add Low Stock Items</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {lowStock.map(item=>(
                <button key={item.id} onClick={()=>addPOItem(item)}
                  disabled={!!poItems.find(i=>i.inventory_item_id===item.id)}
                  style={{padding:'5px 12px',background:poItems.find(i=>i.inventory_item_id===item.id)?C.accentLight:C.amberBg,
                    color:poItems.find(i=>i.inventory_item_id===item.id)?C.accent:C.amber,
                    border:`1px solid ${poItems.find(i=>i.inventory_item_id===item.id)?C.accent:C.amberBorder}`,
                    borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                  {item.item_name} ({item.current_stock} {item.unit})
                </button>
              ))}
              {lowStock.length===0 && <span style={{fontSize:12,color:C.text3}}>No low stock items</span>}
            </div>
          </div>

          {poItems.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:8}}>Order Items ({poItems.length})</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Item','Unit','Curr Stock','Qty to Order','Unit Price','Total',''].map(h=>(
                      <th key={h} style={{padding:'7px 10px',textAlign:'left',color:C.text3,fontWeight:700,borderBottom:`1px solid ${C.border}`}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {poItems.map((item,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:'7px 10px',color:C.text,fontWeight:600}}>{item.item_name}</td>
                      <td style={{padding:'7px 10px',color:C.text3}}>{item.unit}</td>
                      <td style={{padding:'7px 10px',color:item.current_stock<=item.reorder_level?C.red:C.accent,fontWeight:600}}>{item.current_stock}</td>
                      <td style={{padding:'5px'}}><input type="number" value={item.quantity_ordered} onChange={e=>updatePOItem(i,'quantity_ordered',e.target.value)} style={{...inp,padding:'5px 8px',width:80}} /></td>
                      <td style={{padding:'5px'}}><input type="number" value={item.unit_price} onChange={e=>updatePOItem(i,'unit_price',e.target.value)} style={{...inp,padding:'5px 8px',width:80}} /></td>
                      <td style={{padding:'7px 10px',fontWeight:700,color:C.text}}>{inr(item.total_price)}</td>
                      <td><button onClick={()=>setPOItems(p=>p.filter((_,j)=>j!==i))} style={{background:C.redBg,border:'none',color:C.red,borderRadius:6,padding:'3px 8px',cursor:'pointer',fontSize:11}}>Del</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{textAlign:'right',padding:'10px 0',fontSize:14,fontWeight:800,color:C.text}}>
                Total: {inr(poItems.reduce((s,i)=>s+parseFloat(i.total_price||0),0))}
              </div>
            </div>
          )}

          <button onClick={savePO} disabled={saving||!poItems.length}
            style={{width:'100%',padding:'13px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',opacity:(saving||!poItems.length)?0.5:1}}>
            {saving?'Creating...':'Create Purchase Order'}
          </button>
        </div>
      </div>
    );
  };

  const ReceiveModal = ({po}) => {
    const [received, setReceived] = useState((po.items||[]).map(i=>({...i, received_qty: i.quantity_ordered})));
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
        <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:600,padding:28}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text,marginBottom:16}}>Receive Stock — {po.po_number}</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13,marginBottom:16}}>
            <thead>
              <tr style={{background:C.bg}}>
                {['Item','Ordered','Received Qty'].map(h=>(
                  <th key={h} style={{padding:'8px 12px',textAlign:'left',color:C.text3,fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {received.map((item,i)=>(
                <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{padding:'8px 12px',color:C.text,fontWeight:600}}>{item.item_name}</td>
                  <td style={{padding:'8px 12px',color:C.text3}}>{item.quantity_ordered} {item.unit}</td>
                  <td style={{padding:'5px'}}>
                    <input type="number" value={item.received_qty}
                      onChange={e=>setReceived(p=>p.map((r,j)=>j===i?{...r,received_qty:e.target.value}:r))}
                      style={{...inp,padding:'6px 10px',width:100}} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>receivePO(po,received)}
              style={{flex:1,padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
              Confirm Receipt & Update Stock
            </button>
            <button onClick={()=>setShowReceive(null)}
              style={{flex:1,padding:'12px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:14,fontWeight:600,cursor:'pointer'}}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{padding:'24px 28px',background:C.bg,minHeight:'100vh'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>Inventory Management</div>
          <div style={{fontSize:13,color:C.text3,marginTop:4}}>{items.length} items · {lowStock.length} low stock · {outOfStock.length} out of stock</div>
        </div>
        <div style={{display:'flex',gap:10}}>
          {tab==='Stock' && <>
            <button onClick={autoGeneratePO} disabled={generating}
              style={{padding:'9px 16px',background:lowStock.length>0?C.amberBg:C.bg,color:lowStock.length>0?C.amber:C.text3,border:`1px solid ${lowStock.length>0?C.amberBorder:C.border}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
              {generating?'Generating...':` Auto-Gen PO (${lowStock.length} items)`}
            </button>
            <button onClick={()=>{setShowAdd(true);setEditItem(null);setForm(initForm);}}
              style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
              + Add Item
            </button>
          </>}
          {tab==='Purchase Orders' && (
            <button onClick={()=>setShowPOForm(true)}
              style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
              + New PO
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:msg.includes('Error')||msg.includes('Failed')?C.redBg:C.accentLight,color:msg.includes('Error')||msg.includes('Failed')?C.red:C.accent,border:`1px solid ${msg.includes('Error')||msg.includes('Failed')?C.red:C.accent}`}}>{msg}</div>}

      {/* Alert Banners */}
      {lowStock.length > 0 && tab==='Stock' && (
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',background:C.amberBg,border:`1px solid ${C.amberBorder}`,borderRadius:10,marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:C.amber}}>{lowStock.length} items below reorder level: {lowStock.slice(0,5).map(i=>i.item_name).join(', ')}{lowStock.length>5?'...':''}</div>
          <button onClick={autoGeneratePO} disabled={generating}
            style={{padding:'6px 14px',background:C.amber,color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
            Generate PO Now
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:20}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{padding:'8px 16px',background:tab===t?C.accent:C.surface,color:tab===t?'#fff':C.text2,border:`1px solid ${tab===t?C.accent:C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>
            {t} {t==='Purchase Orders'&&pos.length>0?`(${pos.length})`:''}
          </button>
        ))}
      </div>

      {/* STOCK TAB */}
      {tab==='Stock' && (
        <>
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search items..."
              style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13,width:220}} />
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
              style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13}}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
            <select value={filterAlert} onChange={e=>setFilterAlert(e.target.value)}
              style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13}}>
              <option value="">All Status</option>
              <option value="out">Out of Stock</option>
              <option value="low">Low Stock</option>
              <option value="ok">In Stock</option>
            </select>
            <button onClick={loadAll} style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>Refresh</button>
          </div>

          {loading ? <div style={{textAlign:'center',padding:60,color:C.text3}}>Loading...</div> : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Item','Category','Unit','Current Stock','Reorder Level','Avg Cost','Vendor','Status','Actions'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase',letterSpacing:'.05em'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item,i)=>{
                    const status = getStockStatus(item);
                    return (
                      <tr key={item.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                        <td style={{padding:'10px 14px',fontWeight:700,color:C.text}}>{item.item_name}</td>
                        <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{item.category}</td>
                        <td style={{padding:'10px 14px',color:C.text3}}>{item.unit}</td>
                        <td style={{padding:'10px 14px',fontWeight:700,color:status.color==='red'?C.red:status.color==='amber'?C.amber:C.accent,fontSize:14}}>
                          {item.current_stock}
                        </td>
                        <td style={{padding:'10px 14px',color:C.text3}}>{item.reorder_level||'—'}</td>
                        <td style={{padding:'10px 14px',color:C.text2}}>{item.average_cost?inr(item.average_cost):'—'}</td>
                        <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{item.vendor_name||'—'}</td>
                        <td style={{padding:'10px 14px'}}><Badge color={status.color}>{status.label}</Badge></td>
                        <td style={{padding:'10px 14px'}}>
                          <div style={{display:'flex',gap:6}}>
                            <button onClick={()=>{setEditItem(item);setForm({...initForm,...item});setShowAdd(true);}}
                              style={{padding:'4px 10px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>Edit</button>
                            <button onClick={()=>deleteItem(item.id)}
                              style={{padding:'4px 10px',background:C.redBg,color:C.red,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>Del</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length===0 && <div style={{textAlign:'center',padding:40,color:C.text3}}>No items found</div>}
            </div>
          )}
        </>
      )}

      {/* PURCHASE ORDERS TAB */}
      {tab==='Purchase Orders' && (
        <div>
          {pos.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No purchase orders yet. Click "New PO" or use "Auto-Gen PO" from Stock tab.
            </div>
          ) : (
            pos.map(po=>(
              <div key={po.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
                      <div style={{fontSize:16,fontWeight:800,color:C.text}}>{po.po_number}</div>
                      <Badge color={po.status==='Approved'?'accent':po.status==='Received'?'blue':po.status==='Rejected'?'red':'amber'}>
                        {po.status}
                      </Badge>
                      {po.auto_generated && <Badge color='blue'>Auto-Generated</Badge>}
                    </div>
                    <div style={{fontSize:12,color:C.text3}}>
                      Vendor: <strong style={{color:C.text2}}>{po.vendor_name}</strong> · 
                      Raised by: {po.raised_by} · 
                      {new Date(po.created_at).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:18,fontWeight:800,color:C.accent}}>{inr(po.total_amount)}</div>
                    <div style={{fontSize:11,color:C.text3}}>{(po.items||[]).filter(i=>i&&i.item_name).length} items</div>
                  </div>
                </div>

                {/* PO Items */}
                {(po.items||[]).filter(i=>i&&i.item_name).length>0 && (
                  <div style={{background:C.bg,borderRadius:8,padding:12,marginBottom:12}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr>
                          {['Item','Unit','Curr Stock','Reorder Level','Qty Ordered','Unit Price','Total'].map(h=>(
                            <th key={h} style={{padding:'6px 10px',textAlign:'left',color:C.text3,fontWeight:700}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(po.items||[]).filter(i=>i&&i.item_name).map((item,i)=>(
                          <tr key={i} style={{borderTop:`1px solid ${C.border}`}}>
                            <td style={{padding:'6px 10px',color:C.text,fontWeight:600}}>{item.item_name}</td>
                            <td style={{padding:'6px 10px',color:C.text3}}>{item.unit}</td>
                            <td style={{padding:'6px 10px',color:parseFloat(item.current_stock)<=parseFloat(item.reorder_level)?C.red:C.accent,fontWeight:600}}>{item.current_stock}</td>
                            <td style={{padding:'6px 10px',color:C.text3}}>{item.reorder_level}</td>
                            <td style={{padding:'6px 10px',color:C.text,fontWeight:700}}>{item.quantity_ordered}</td>
                            <td style={{padding:'6px 10px',color:C.text2}}>{inr(item.unit_price)}</td>
                            <td style={{padding:'6px 10px',color:C.accent,fontWeight:700}}>{inr(item.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Actions */}
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {po.status==='Draft' && <>
                    <button onClick={()=>updatePOStatus(po.id,'Approved')}
                      style={{padding:'7px 14px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Approve</button>
                    <button onClick={()=>updatePOStatus(po.id,'Sent to Vendor')}
                      style={{padding:'7px 14px',background:C.blueBg,color:C.blue,border:`1px solid ${C.blue}`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Send to Vendor</button>
                    <button onClick={()=>updatePOStatus(po.id,'Rejected')}
                      style={{padding:'7px 14px',background:C.redBg,color:C.red,border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Reject</button>
                  </>}
                  {(po.status==='Approved'||po.status==='Sent to Vendor') && (
                    <button onClick={()=>setShowReceive(po)}
                      style={{padding:'7px 14px',background:C.accentLight,color:C.accent,border:`1px solid ${C.accent}`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Receive Stock</button>
                  )}
                  <button onClick={()=>window.print()}
                    style={{padding:'7px 14px',background:C.bg,color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>Print PO</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* TRANSACTIONS TAB */}
      {tab==='Transactions' && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text2,marginBottom:16}}>Stock transaction history coming from PO receipts and manual adjustments</div>
          <div style={{textAlign:'center',padding:40,color:C.text3}}>Transactions will appear here as stock moves in and out</div>
        </div>
      )}

      {/* SETTINGS TAB */}
      {tab==='Settings' && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24,maxWidth:500}}>
          <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:16}}>Inventory Settings</div>
          <div style={{fontSize:13,color:C.text2,lineHeight:1.8}}>
            <div>Auto PO generation: <strong style={{color:C.accent}}>Enabled</strong></div>
            <div>Low stock alert threshold: <strong>At or below reorder level</strong></div>
            <div>PO approval required: <strong>Yes — GM approval</strong></div>
            <div>Email alerts: <strong>Configure in Notifications</strong></div>
          </div>
        </div>
      )}

      {showAdd && <ItemForm />}
      {showPOForm && <POForm />}
      {showReceive && <ReceiveModal po={showReceive} />}
    </div>
  );
}
